/**
 * ClientFundsGridWidget
 *
 * Dashboard widget that displays advisor client funds as tiles.
 * Self-contained - fetches its own data via useAdvisorAccess hook.
 * Includes:
 * - Tabs for All/Active/Pending funds
 * - Pending invites with resend/cancel functionality
 */
import { useState } from 'react'
import { useQuery } from '@apollo/client'
import { Typography, Box, LinearProgress, Snackbar, Alert } from '@mui/material'

import { useAdvisorAccess } from '../../../hooks/useAdvisorAccess'
import { ClientFundGrid } from '../../advisor'
import { Stack, Text } from '../..'
import { queries } from '../../../graphql'

export function ClientFundsGridWidget() {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

  const { accessibleFunds, loading, error, refetch: refetchFunds } = useAdvisorAccess()

  const {
    data: invitesData,
    loading: invitesLoading,
    refetch: refetchInvites,
  } = useQuery(queries.fundClaims.advisorFundClaimInvites, {
    fetchPolicy: 'cache-and-network',
  })

  const {
    data: securitiesInvitesData,
    loading: securitiesInvitesLoading,
    refetch: refetchSecuritiesInvites,
  } = useQuery(queries.onboarding.myPendingAdvisorInvitations, {
    fetchPolicy: 'cache-and-network',
  })

  // Filter pending/expired invites (those not yet claimed and advisor doesn't have fund access)
  const pendingInvites =
    invitesData?.advisorFundClaimInvites?.filter(
      (invite) => ['pending', 'expired'].includes(invite.statusLabel) && !invite.advisorHasFundAccess
    ) || []

  const declinedInvites =
    invitesData?.advisorFundClaimInvites?.filter((invite) => invite.statusLabel === 'declined') || []

  const securitiesInvites = securitiesInvitesData?.myPendingAdvisorInvitations || []

  const handleResendSuccess = (message) => {
    setSnackbar({ open: true, message, severity: 'success' })
    refetchInvites()
  }

  const handleCancelSuccess = (message) => {
    setSnackbar({ open: true, message, severity: 'success' })
    refetchInvites()
    refetchFunds()
  }

  const handleAcceptSuccess = (message) => {
    setSnackbar({ open: true, message, severity: 'success' })
    refetchSecuritiesInvites()
    refetchFunds()
  }

  const handleReinviteSuccess = (message) => {
    setSnackbar({ open: true, message, severity: 'success' })
    refetchSecuritiesInvites()
    refetchInvites()
    refetchFunds()
  }

  const handleSnackbarClose = () => setSnackbar((prev) => ({ ...prev, open: false }))

  if (loading || invitesLoading || securitiesInvitesLoading) {
    return <LinearProgress />
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Error loading client funds: {error.message}</Typography>
      </Box>
    )
  }

  return (
    <Stack>
      <Text.H2>Client&apos;s Giving Funds</Text.H2>
      <Text.Body color="text.secondary" sx={{ mb: 2 }}>
        Select a fund to view or manage on behalf of your client.
      </Text.Body>

      <ClientFundGrid
        funds={accessibleFunds}
        pendingInvites={pendingInvites}
        declinedInvites={declinedInvites}
        securitiesInvites={securitiesInvites}
        onResendSuccess={handleResendSuccess}
        onCancelSuccess={handleCancelSuccess}
        onAcceptSuccess={handleAcceptSuccess}
        onReinviteSuccess={handleReinviteSuccess}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  )
}
